import { useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Col, Row, getItemWithExpiration } from '../../../../lib/utils';
import { Body2Regular, Body3Bold, Body3Regular, CaptionLight, Subtitle1Bold } from '../../../../lib/font';
import { colors } from '../../../../lib/colors';
import MemberListHeader from '../../../../components/MemberList/MemberListHeader';
import { useNavigate } from 'react-router-dom';
import {
    GetCenterMemberListRemainingPeriod,
    GetCenterMemberListRemainingSessions,
    GetCenterMemberListStatus,
} from '../../../../api/center';
import { CodeValueType } from '../../../../types/types';
import Grid, { GridColProps } from '../../../Grid/Grid';
import dayjs from 'dayjs';
import TrashButton from '../../../Button/TrashButton';
import ModalAlert from '../../../modal/ModalAlert';
import SvgIcon from '../../../SvgIcon';
import WhiteSquareButton from '../../../Button/WhiteSquareButton';
import ColorSquareButton from '../../../Button/ColorSquareButton';
import useCenterMutationService from '../../../../services/useCenterMutationService';
import ModalTrainerInfomationMemberListMultiCoachChange from '@/components/MemberProfile/ModalTrainerInfomationMemberListMultiCoachChange';
import { fetchCoachNameList } from '@/services/useCenterQueryService';
import useMultipleQueries from '@/hook/useMultipleQueries';
import ModalMemberListProgress from '@/components/modal/ModalMemberListProgress';
import { formatKoreanPhoneNumber } from '@/utils/phoneNumber';
import ActivePassSummary, { ActivePassSummaryItem } from '@/components/MemberList/ActivePassSummary';
import { usePublicMemberList } from '@/features/publicMember/hooks';
import { ICenterMember } from '@/features/publicMember/types';
import { publicMemberKeys } from '@/features/publicMember/keys';
import AppAccountStatusBadge from '@/components/MemberList/AppAccountStatusBadge';
import { getBulkSkippedMembershipCount, getMembershipRecoveryNotice } from '@/lib/membershipRecovery';
import {
    BulkChangeCoachResult,
    ChangeCoachFailure,
    getBulkChangeCoachResult,
    getChangeCoachErrorCode,
} from '@/lib/changeCoachError';
import BulkChangeCoachResultModal, {
    BulkChangeCoachFailureModal,
} from '@/components/MemberProfile/BulkChangeCoachResultModal';

const formatMembershipSummaryPeriod = (startDate?: string, endDate?: string) => {
    const start = startDate ? dayjs(startDate).format('YY.MM.DD') : '';
    const end = endDate ? dayjs(endDate).format('YY.MM.DD') : '';
    if (start && end) return `${start} ~ ${end}`;
    if (start) return `${start} ~`;
    if (end) return `~ ${end}`;
    return undefined;
};

type BadgeStyle = {
    label: string;
    bg: string;
    color: string;
    border: string;
};

const Badge = ({ label, bg, color, border }: BadgeStyle) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '6px 12px',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 700,
            backgroundColor: bg,
            color,
            border: `1px solid ${border}`,
            letterSpacing: 0,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
        }}
    >
        {label}
    </span>
);

const EmptyCell = ({ label = '—' }: { label?: string } = {}) => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 34,
                height: 24,
                padding: '0 10px',
                borderRadius: 999,
                backgroundColor: '#F8FAFC',
                color: '#CBD5E1',
                fontSize: 13,
                fontWeight: 700,
            }}
        >
            {label}
        </span>
    </div>
);

type MemberListStatePanelProps = {
    title: string;
    description: string;
    tone?: 'loading' | 'error';
    onRetry?: () => void;
};

const MemberListStatePanel = ({ title, description, tone = 'loading', onRetry }: MemberListStatePanelProps) => {
    const isError = tone === 'error';
    return (
        <div
            style={{
                width: '100%',
                minHeight: 260,
                border: '1px solid #E5EAF3',
                borderRadius: 8,
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
            }}
        >
            <div
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    border: isError ? '1px solid #FECACA' : '3px solid #DBEAFE',
                    borderTopColor: isError ? '#FECACA' : '#2563EB',
                    backgroundColor: isError ? '#FEF2F2' : '#EFF6FF',
                }}
            />
            <Body3Bold style={{ color: isError ? '#DC2626' : colors.LAVEL_4 }}>{title}</Body3Bold>
            <Body3Regular style={{ color: colors.LAVEL_2 }}>{description}</Body3Regular>
            {onRetry && (
                <button
                    type="button"
                    onClick={onRetry}
                    style={{
                        marginTop: 8,
                        height: 36,
                        padding: '0 16px',
                        borderRadius: 8,
                        border: '1px solid #BFDBFE',
                        backgroundColor: '#EFF6FF',
                        color: '#2563EB',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}
                >
                    다시 불러오기
                </button>
            )}
        </div>
    );
};

const MemberListFetchBanner = ({ text, tone = 'loading' }: { text: string; tone?: 'loading' | 'error' }) => (
    <div
        style={{
            width: '100%',
            minHeight: 42,
            borderRadius: 8,
            border: `1px solid ${tone === 'error' ? '#FECACA' : '#BFDBFE'}`,
            backgroundColor: tone === 'error' ? '#FEF2F2' : '#EFF6FF',
            color: tone === 'error' ? '#DC2626' : '#2563EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
            marginBottom: 10,
        }}
    >
        {text}
    </div>
);

const getTrainerMemberStatusStyle = (status?: string): BadgeStyle => {
    switch (status) {
        case 'active':
            return {
                label: '활성',
                bg: '#ECFDF5',
                color: '#059669',
                border: '#A7F3D0',
            };
        case 'expired':
            return {
                label: '만료',
                bg: '#FEF2F2',
                color: '#DC2626',
                border: '#FECACA',
            };
        case 'none':
        case 'empty':
            return {
                label: '대기',
                bg: '#FFFBEB',
                color: '#D97706',
                border: '#FDE68A',
            };
        default:
            return {
                label: '비활성',
                bg: '#F3F4F6',
                color: '#6B7280',
                border: '#E5E7EB',
            };
    }
};

const isActiveTicket = (t?: { status?: string }) => t?.status === 'active';

// PT 수강권(v1 memberships) → 요약 아이템
const toPtSummaryItems = (
    memberships: NonNullable<ICenterMember['memberships']> = []
): ActivePassSummaryItem[] =>
    memberships.filter(isActiveTicket).map((m) => ({
        id: m.membershipId,
        name: m.name,
        current: m.currentSession,
        total: m.totalSession,
        remaining: m.remainSession,
        coachName: m.coachName,
        periodText: formatMembershipSummaryPeriod(m.activationDate, m.expirationDate),
        expiresAt: m.expirationDate,
        tone: 'pt',
    }));

// 그룹수강권(v1 groupTickets) → 요약 아이템 (totalCount/usedCount/remainingCount 기준)
const toGroupSummaryItems = (
    tickets: NonNullable<ICenterMember['groupTickets']> = []
): ActivePassSummaryItem[] =>
    tickets.filter(isActiveTicket).map((t) => {
        const total = Math.max(Number(t.totalCount ?? 0), Number(t.usedCount ?? 0) + Number(t.remainingCount ?? 0), 0);
        const remaining = Math.max(Number(t.remainingCount ?? 0), 0);
        return {
            id: t.memberTicketId,
            name: t.name,
            current: total > 0 ? Math.min(Math.max(total - remaining, 0), total) : undefined,
            total,
            remaining,
            periodText: formatMembershipSummaryPeriod(t.startDate, t.expiryDate),
            expiresAt: t.expiryDate,
            tone: 'group',
        };
    });

// v1 응답 필드로 회원 표시 상태 산출 (신 회원목록 페이지 getMemberDisplayStatus 와 동일 정책)
const getDisplayStatusKey = (m: ICenterMember): 'active' | 'expired' | 'empty' | 'none' => {
    switch (m.ptStatus) {
        case 'active':
            return 'active';
        case 'expired':
            return 'expired';
        case 'empty':
            return 'empty';
        case 'none':
        default:
            if (m.hasSubscription) return 'active';
            if ((m.groupTicketCount ?? 0) > 0) return 'active';
            if (m.subscriptionCount > 0) return 'expired';
            if (m.hasGroupTicket && (m.groupTicketCount ?? 0) === 0) return 'expired';
            return 'none';
    }
};

// 회원상태 드롭다운(all/active/expired/none) 매칭. none 은 대기(empty)·없음(none) 모두 포함.
const matchesStatusFilter = (m: ICenterMember, code: string) => {
    if (code === 'all') return true;
    const key = getDisplayStatusKey(m);
    if (code === 'none') return key === 'none' || key === 'empty';
    return key === code;
};

// 활성 수강권(PT+그룹)의 만료일 목록
const getActiveExpiryDates = (m: ICenterMember): string[] => {
    const pt = (m.memberships ?? []).filter(isActiveTicket).map((x) => x.expirationDate);
    const group = (m.groupTickets ?? []).filter(isActiveTicket).map((x) => x.expiryDate);
    return [...pt, ...group].filter((d): d is string => !!d);
};

// 활성 수강권(PT+그룹)의 잔여 횟수 목록
const getActiveRemainingSessions = (m: ICenterMember): number[] => {
    const pt = (m.memberships ?? []).filter(isActiveTicket).map((x) => Number(x.remainSession ?? 0));
    const group = (m.groupTickets ?? []).filter(isActiveTicket).map((x) => Number(x.remainingCount ?? 0));
    return [...pt, ...group];
};

// 관리자 회원목록(center.controller.ts getCenterMembersV3)과 같은 판정을 클라에서 한다.
// 잔여 일수로 환산하지 않고 만료일을 직접 비교하는 이유: diff 는 0 쪽으로 잘려서
// '어제 만료된 건'과 '오늘 만료 예정인 건'이 똑같이 0일이 되어 둘을 구분할 수 없다.
const isPastDue = (expiresAt: string) => !dayjs(expiresAt).isAfter(dayjs());
const isExpiringWithin = (expiresAt: string, days: number) => {
    const target = dayjs(expiresAt);
    // 하한(오늘 이후)이 있어야 이미 만료된 건이 '만료 임박'으로 새지 않는다. 'expired' 와 상호배타.
    return target.isAfter(dayjs()) && !target.isAfter(dayjs().add(days, 'day').endOf('day'));
};

// 구 헤더의 '미만/이상' 버킷 → 클라이언트 필터 술어
const PERIOD_PREDICATES: Record<string, (expiresAt: string) => boolean> = {
    expired: (e) => isPastDue(e),
    lessThanOneWeek: (e) => isExpiringWithin(e, 7),
    lessThanTwoWeeks: (e) => isExpiringWithin(e, 14),
    lessThanOneMonth: (e) => isExpiringWithin(e, 30),
    lessThanHundredDays: (e) => isExpiringWithin(e, 100),
    lessThanOneYear: (e) => isExpiringWithin(e, 365),
    oneYearOrMore: (e) => !isPastDue(e) && !isExpiringWithin(e, 365),
};

const SESSION_PREDICATES: Record<string, (s: number) => boolean> = {
    lessThanThreeTimes: (s) => s < 3,
    lessThanFiveTimes: (s) => s < 5,
    lessThanTenTimes: (s) => s < 10,
    lessThanFifteenTimes: (s) => s < 15,
    lessThanTwentyTimes: (s) => s < 20,
    twentyTimesOrMore: (s) => s >= 20,
};

const MemberList = ({ coachId, createAlert }: any) => {
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { patchDisconnectMembersMutation } = useCenterMutationService({
        onPatchDisconnectMembersSuccess() {
            queryClient.invalidateQueries({
                queryKey: publicMemberKeys.list(authObject?.centerId),
            });
        },
        onPatchDisconnectMemberError() {},
    });
    // 진행률 모달 상태
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [isVirtualComplete, setIsVirtualComplete] = useState<boolean>(false);
    const [isApiComplete, setIsApiComplete] = useState<boolean>(false);
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
    // 일괄 변경 요청 시점의 회원 이름. 서버 결과 행에는 memberId 만 있어 실패 안내에 이름을 붙일 때 쓴다.
    const bulkChangeMemberNamesRef = useRef<Map<string, string>>(new Map());
    const lastBulkChangeRequestRef = useRef<{ centerId: string; memberIds: string[]; newCoachId: string } | null>(null);
    const completeIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [bulkChangeResult, setBulkChangeResult] = useState<{
        result: BulkChangeCoachResult;
        recoveryNotice: string;
    } | null>(null);
    const [bulkChangeFailure, setBulkChangeFailure] = useState<{
        failure: ChangeCoachFailure;
        occurredAt: Date;
    } | null>(null);

    const startVirtualProgress = (memberCount: number) => {
        const estimatedSeconds = memberCount * 1.2; // 1.2초/명
        const totalSteps = Math.max(1, Math.floor(estimatedSeconds * 10)); // 0.1초간격
        const stepSize = 100 / totalSteps;
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = setInterval(() => {
            setProgress((prev) => {
                const next = prev + stepSize;
                if (next >= 100) {
                    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
                    setIsVirtualComplete(true);
                    return 100;
                }
                return next;
            });
        }, 100);
    };

    const completeProgressToFull = () => {
        // 가상 진행률 interval 정리
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        if (completeIntervalRef.current) clearInterval(completeIntervalRef.current);

        // 함수형 업데이트로 최신 progress 값 확인
        setProgress((prev) => {
            // progress가 이미 100%인 경우 즉시 종료
            if (prev >= 100) {
                setIsProcessing(false);
                return 100;
            }

            // progress가 100% 미만인 경우 interval로 100%까지 진행
            const steps = 10; // 1초에 걸쳐 0.1초씩
            const remaining = Math.max(0, 100 - prev);
            const step = remaining / steps;
            let count = 0;
            
            completeIntervalRef.current = setInterval(() => {
                count += 1;
                setProgress((current) => {
                    const next = current + step;
                    if (count >= steps || next >= 100) {
                        if (completeIntervalRef.current) clearInterval(completeIntervalRef.current);
                        setIsProcessing(false);
                        return 100;
                    }
                    return Math.min(100, next);
                });
            }, 100);
            
            return prev;
        });
    };

    const handleApiCompletion = () => {
        setIsApiComplete(true);
        // 함수형 업데이트로 최신 progress 값 확인하여 처리
        setProgress((prev) => {
            if (prev >= 100) {
                // progress가 이미 100%이면 즉시 종료
                setIsProcessing(false);
                return 100;
            } else {
                // progress가 100% 미만이면 completeProgressToFull 호출
                completeProgressToFull();
                return prev;
            }
        });
    };

    const { patchChangeCoachForMembersMutation } = useCenterMutationService({
        onPatchChangeCoachForMembersSuccess(data) {
            handleApiCompletion();
            // 선택한 강사 이름을 가져오기
            const newCoachName = selectedNewCoachName || '변경된';
            // 일괄 변경은 회원별 결과가 results[] 에 담긴다. 되살리지 못한 수강권을 전부 합쳐
            // 한 번에 알린다 — 회원별로 알럿을 띄우면 수십 개가 쌓인다(U9).
            const skippedNotice = getMembershipRecoveryNotice(getBulkSkippedMembershipCount(data));
            // HTTP 200 이어도 회원별로 실패할 수 있다. 회원별 결과는 작은 알림 대신
            // 성공·실패를 한눈에 볼 수 있는 결과 모달로 보여준다.
            const result = getBulkChangeCoachResult(data, (memberId) =>
                bulkChangeMemberNamesRef.current.get(memberId)
            );
            // 진행률 애니메이션(최대 1초) 종료 후 알림
            setTimeout(() => {
                setIsVisibleMultiCoachChange(false);
                setBulkChangeFailure(null);
                if (result) {
                    setBulkChangeResult({ result, recoveryNotice: skippedNotice });
                } else {
                    createAlert(
                        '담당 강사 변경이 완료되었어요!',
                        `선택된 회원님들의 담당 강사님을 ${newCoachName} 강사님으로 변경완료했어요! ${skippedNotice}`,
                        '확인',
                        () => {}
                    );
                }
                queryClient.invalidateQueries({
                    queryKey: publicMemberKeys.list(authObject?.centerId),
                });
            }, 1000);
        },
        onPatchChangeCoachForMembersError(error) {
            // 모든 인터벌 정리
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
            if (completeIntervalRef.current) clearInterval(completeIntervalRef.current);

            // 모든 상태 초기화
            setIsProcessing(false);
            setProgress(0);
            setIsVirtualComplete(false);
            setIsApiComplete(false);

            // 모달 닫기
            setIsVisibleMultiCoachChange(false);

            // 요청 전체 실패도 단건 카드와 같은 톤으로 원인과 다음 행동을 안내한다.
            setBulkChangeFailure({ failure: error, occurredAt: new Date() });
        },
    });

    const { refinedResults } = useMultipleQueries([
        {
            fetchFn: fetchCoachNameList,
            params: { centerId: authObject?.centerId, isAllConcat: true },
            enabled: !!authObject?.centerId,
        },
    ]);

    const dropDownCoachList = useMemo(
        () => (refinedResults?.[0]?.data as CodeValueType<string>[]) ?? [],
        [refinedResults]
    );

    const [selectCoach, setSelectCoach] = useState<CodeValueType | undefined>(undefined);

    useEffect(() => {
        if (dropDownCoachList && dropDownCoachList.length > 0 && coachId) {
            const foundCoach = dropDownCoachList.find((item: CodeValueType) => item.code === coachId);
            // 이전 값과 다를 때만 업데이트하여 무한 루프 방지
            if (foundCoach?.code !== selectCoach?.code) {
                setSelectCoach(foundCoach);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coachId, dropDownCoachList]);

    // 선택삭자 모달 상태값
    const [isVisibleDeleteMember, setIsVisibleDeleteMember] = useState<boolean>(false);
    const [isVisibleMultiCoachChange, setIsVisibleMultiCoachChange] = useState<boolean>(false);
    const [selectedNewCoachName, setSelectedNewCoachName] = useState<string>('');
    // #region 필터 상태값
    // 회원 리스트
    const [memberList, setMemberList] = useState<ICenterMember[]>([]);
    const [selectedMemberList, setSelectedMemberList] = useState<ICenterMember[]>([]);
    const [selectedMemberKeys, setSelectedMemberKeys] = useState<Set<string>>(new Set());

    // 회원의 상태
    const [memberStatus, setMemberStatus] = useState<CodeValueType<GetCenterMemberListStatus>>({
        code: 'all',
        value: '회원 상태',
    });
    // 수강권 잔여기간
    const [remainingPeriod, setRemainingPeriod] = useState<CodeValueType<GetCenterMemberListRemainingPeriod>>({
        code: 'all',
        value: '잔여 기간',
    });
    // 수강권 잔여 횟수
    const [remainingSessions, setRemainingSessions] = useState<CodeValueType<GetCenterMemberListRemainingSessions>>({
        code: 'all',
        value: '잔여 횟수',
    });

    // #endregion

    // 초기화 버튼
    const onClickInit = () => {
        // 상태값들 초기로
        setMemberStatus({
            code: 'all',
            value: '회원 상태',
        });
        setRemainingPeriod({
            code: 'all',
            value: '잔여 기간',
        });
        setRemainingSessions({
            code: 'all',
            value: '잔여 횟수',
        });
    };

    // 진행 중 페이지 이탈 방지
    useEffect(() => {
        if (!isProcessing) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isProcessing]);

    const onDeleteSelectedMember = () => {
        patchDisconnectMembersMutation.mutate({
            centerId: authObject?.centerId,
            memberIds: selectedMemberList.map((member) => member.memberId),
            accessToken: authorization,
        });
    };

    const runBulkChangeRequest = (request: { centerId: string; memberIds: string[]; newCoachId: string }) => {
        if (patchChangeCoachForMembersMutation.isLoading || isProcessing) return;
        setBulkChangeFailure(null);
        setBulkChangeResult(null);
        setIsProcessing(true);
        setProgress(0);
        setIsVirtualComplete(false);
        setIsApiComplete(false);
        startVirtualProgress(request.memberIds.length);
        patchChangeCoachForMembersMutation.mutate(request);
    };

    const startBulkChange = (newCoachId: string) => {
        if (!newCoachId || !authObject?.centerId || selectedMemberList.length === 0) return;
        const selectedCoach = dropDownCoachList.find((item) => item.code === newCoachId);
        setSelectedNewCoachName(selectedCoach?.value || '');
        bulkChangeMemberNamesRef.current = new Map(
            selectedMemberList.map((member) => [member.memberId, member.name])
        );
        const request = {
            centerId: authObject.centerId,
            memberIds: selectedMemberList.map((member) => member.memberId),
            newCoachId,
        };
        lastBulkChangeRequestRef.current = request;
        runBulkChangeRequest(request);
    };
    // v1 통합 회원목록 API. 회원 단위로 수강권을 집계하므로 인계(coachId 변경)된 수강권도 정확히 포함되어
    // 강사 프로필 회원목록과 회원관리 페이지의 상태(활성/만료)가 일치한다.
    // coachId를 직접 전달해 백엔드가 센터 전체 회원을 훑지 않고 담당 회원으로 먼저 좁히게 한다.
    const {
        data: publicMemberData,
        isLoading: isPublicMemberLoading,
        isFetching: isPublicMemberFetching,
        isError: isPublicMemberError,
        refetch: refetchPublicMemberList,
    } = usePublicMemberList({
        centerId: authObject?.centerId,
        coach: coachId,
        page: 1,
        limit: 5000,
        timeout: 60_000,
        enabled: !!authObject?.centerId && !!authorization && !!coachId,
    });

    const allMembers = useMemo<ICenterMember[]>(() => publicMemberData?.results ?? [], [publicMemberData]);

    // 회원상태/잔여기간/잔여횟수 드롭다운은 구 헤더의 '미만/이상' 버킷이라 클라이언트에서 필터링한다.
    useEffect(() => {
        const periodPredicate = PERIOD_PREDICATES[remainingPeriod.code];
        const sessionPredicate = SESSION_PREDICATES[remainingSessions.code];
        const filtered = allMembers.filter((m) => {
            if (!matchesStatusFilter(m, memberStatus.code)) return false;
            if (periodPredicate && !getActiveExpiryDates(m).some(periodPredicate)) return false;
            if (sessionPredicate && !getActiveRemainingSessions(m).some(sessionPredicate)) return false;
            return true;
        });
        setMemberList(filtered);
    }, [allMembers, memberStatus.code, remainingPeriod.code, remainingSessions.code]);

    const isWaitingForRequestParams = !authObject?.centerId || !authorization || !coachId;
    const isMemberListInitialLoading = (isWaitingForRequestParams || isPublicMemberLoading) && memberList.length === 0;
    const isMemberListLoadError = isPublicMemberError && memberList.length === 0;
    const isMemberListRefetching = isPublicMemberFetching && !isMemberListInitialLoading;
    const isMemberListStaleError = isPublicMemberError && memberList.length > 0;

    // #region 그리드 컬럼
    const colDefs = useMemo<GridColProps[]>(() => [
        {
            field: 'name',
            headerName: '이름',
            align: 'center',
            width: 150,
            fontType: 'Body2Bold',
            isSort: true,
        },
        {
            field: 'ptStatus',
            headerName: '상태',
            width: 90,
            align: 'center',
            customComponent: (_value: any, row: ICenterMember) => {
                const style = getTrainerMemberStatusStyle(getDisplayStatusKey(row));
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Badge {...style} />
                    </div>
                );
            },
        },
        {
            field: 'phoneNumber',
            headerName: '휴대폰 번호',
            width: 170,
            nullValue: '-',
            align: 'center',
            fontType: 'Body2Regular',
            customFormat: (value: string) => formatKoreanPhoneNumber(value, '-'),
        },
        {
            field: 'appAccountStatus',
            headerName: '앱 상태',
            width: 112,
            align: 'center',
            customComponent: (_value: any, row: ICenterMember) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <AppAccountStatusBadge member={row} />
                </div>
            ),
        },
        {
            field: 'memberships',
            headerName: 'PT 수강권',
            width: 360,
            align: 'center',
            customComponent: (_value: any, row: ICenterMember) => {
                const summaryItems = toPtSummaryItems(row.memberships || []);
                if (summaryItems.length === 0) return <EmptyCell />;
                return <ActivePassSummary items={summaryItems} tone="pt" />;
            },
        },
        {
            field: 'groupTickets',
            headerName: '그룹수강권',
            width: 360,
            align: 'center',
            customComponent: (_value: any, row: ICenterMember) => {
                const summaryItems = toGroupSummaryItems(row.groupTickets || []);
                if (summaryItems.length === 0) return <EmptyCell />;
                return <ActivePassSummary items={summaryItems} tone="group" />;
            },
        },
        {
            field: 'activeSubscriptions',
            headerName: '이용권',
            width: 100,
            align: 'center',
            customComponent: (value: any[], row: any) => {
                const subscriptions = Array.isArray(value)
                    ? value
                    : Array.isArray(row?.subscriptions)
                    ? row.subscriptions
                    : [];
                const activeSubscriptions = subscriptions.filter(
                    (subscription: any) => !subscription.status || subscription.status === 'active'
                );
                const count = row?.subscriptionCount || row?.activeSubscriptionCount || activeSubscriptions.length;
                if (!count) return <EmptyCell />;

                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Badge label={`${count}개`} bg="#FFFBEB" color="#B45309" border="#FDE68A" />
                    </div>
                );
            },
        },
        {
            field: 'lastLessonAt',
            headerName: '최근 수업',
            width: 124,
            align: 'center',
            isSort: true,
            customComponent: (value: string) => {
                if (!value || !dayjs(value).isValid()) return <EmptyCell />;
                const date = dayjs(value);
                const daysAgo = Math.max(dayjs().diff(date, 'day'), 0);
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>
                            {date.format('MM.DD')}
                        </span>
                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: daysAgo <= 7 ? '#059669' : daysAgo <= 30 ? '#6B7280' : '#DC2626',
                                backgroundColor: daysAgo <= 7 ? '#ECFDF5' : daysAgo <= 30 ? 'transparent' : '#FEF2F2',
                                padding: daysAgo <= 7 || daysAgo > 30 ? '3px 8px' : '0',
                                borderRadius: 999,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {daysAgo === 0 ? '오늘' : `${daysAgo}일 전`}
                        </span>
                    </div>
                );
            },
        },
        {
            field: 'joinedAt',
            headerName: '가입일',
            width: 120,
            align: 'center',
            isSort: true,
            customComponent: (value: string) => {
                if (!value || !dayjs(value).isValid()) return <EmptyCell />;
                return (
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#6B7280' }}>
                        {dayjs(value).format('YY.MM.DD')}
                    </span>
                );
            },
        },
    ], []);
    // #endregion

    return (
        <Col
            style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'white',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <MemberListHeader
                // coach={coach}
                remainingPeriod={remainingPeriod}
                remainingSessions={remainingSessions}
                memberStatus={memberStatus}
                onMemberStatusChange={(item) => {
                    setMemberStatus(item);
                }}
                onRemainingPeriodChange={(item) => {
                    setRemainingPeriod(item);
                }}
                onRemainingSessionsChange={(item) => {
                    setRemainingSessions(item);
                }}
                onClickInit={onClickInit}
                type="coachProfile"
                onClickMemberRegister={() => navigate('/member/temporary')}
            />

            <Col className="w-[1192px]">
                {isMemberListInitialLoading ? (
                    <MemberListStatePanel
                        title="회원 목록을 불러오고 있어요"
                        description="담당 회원과 수강권 정보를 확인하는 중입니다."
                    />
                ) : isMemberListLoadError ? (
                    <MemberListStatePanel
                        title="회원 목록을 불러오지 못했어요"
                        description="잠시 후 다시 시도해주세요."
                        tone="error"
                        onRetry={() => refetchPublicMemberList()}
                    />
                ) : (
                    <>
                        {isMemberListRefetching && (
                            <MemberListFetchBanner text="회원 목록을 최신 정보로 업데이트하고 있어요." />
                        )}
                        {isMemberListStaleError && (
                            <MemberListFetchBanner
                                text="최신 정보를 불러오지 못해 이전 조회 결과를 표시하고 있어요."
                                tone="error"
                            />
                        )}
                        <Grid
                            colDefs={colDefs}
                            rowData={memberList}
                            onClickRow={(row) => {
                                navigate(`/member/${row.memberId}`, { state: { memberId: row.memberId } });
                            }}
                            onSortedData={(data: any[]) => {
                                setMemberList(data);
                            }}
                            isCheckColumn
                            isAllCheckColumn={false}
                            selectedKeys={selectedMemberKeys}
                            rowKey="memberId"
                            onToggleSelect={(key) => {
                                setSelectedMemberKeys((current) => {
                                    const next = new Set(current);
                                    if (next.has(key)) next.delete(key);
                                    else next.add(key);
                                    return next;
                                });
                            }}
                            tableWidth={1628}
                            rowHeight={52}
                            keepPageHeight
                            // 왼쪽 상단
                            leftTopCustomComponent={(filterData) => {
                                return (
                                    <CaptionLight color={colors.LAVEL_1}>
                                        {`회원 총 ${memberList?.length}명 / 검색결과 : ${filterData?.length}명`}
                                    </CaptionLight>
                                );
                            }}
                            // setSelectedMemberList(selectedData);
                            // setIsVisibleDeleteMember(true);
                            leftBottomCustomComponent={(selectedData) => {
                                const selectedCount = selectedData.length;
                                const hasSelection = selectedCount > 0;

                                return (
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: 8,
                                            opacity: hasSelection ? 1 : 0.45,
                                            pointerEvents: hasSelection ? 'auto' : 'none',
                                        }}
                                    >
                                        <TrashButton
                                            text={`${selectedCount}명 회원 삭제`}
                                            onClick={() => {
                                                setSelectedMemberList(selectedData);
                                                setIsVisibleDeleteMember(true);
                                            }}
                                        />
                                        <TrashButton
                                            text={`${selectedCount}명 담당강사 변경`}
                                            onClick={() => {
                                                setSelectedMemberList(selectedData);
                                                setIsVisibleMultiCoachChange(true);
                                                // createAlert(
                                                //     '현재 해당 기능 보완중입니다',
                                                //     '보완 완료 전 까지 회원 프로필에서 각각 담당강사 변경 부탁드려요',
                                                //     '확인',
                                                //     () => {}
                                                // );
                                            }}
                                            showIcon={false}
                                        />
                                    </div>
                                );
                            }}
                            // 가운데 상단
                            centerTopComponent="pagination"
                            // 가운데 하단
                            centerBottomComponent="pagination"
                            // 오른쪽 상단
                            rightTopComponent="textInput"
                            // 오른쪽 하단
                            rightBottomComponent="dropdown"
                            textInputPlaceholder="내용을 입력해주세요"
                            mb={200}
                            isEnterChange
                        />
                    </>
                )}
            </Col>
            {isVisibleDeleteMember && (
                <ModalAlert
                    modalVisible={isVisibleDeleteMember}
                    setModalVisible={setIsVisibleDeleteMember}
                    customComponent={
                        <Col className="justify-center items-center w-[470px] h-[360px]">
                            <SvgIcon name={'SvgError'} fill="red" style={{ marginBottom: 8 }} />
                            <Subtitle1Bold style={{ color: colors.LAVEL_4, marginBottom: 12 }}>
                                {`${selectedMemberList.length}명의 선택된 회원 삭제`}
                            </Subtitle1Bold>
                            <Body2Regular style={{ marginBottom: 16 }}>선택된 회원이 모두 삭제됩니다!</Body2Regular>
                            <Col className="p-[16px] bg-WHITE_500 rounded-[8px] mb-[20px] w-full">
                                <Body3Regular>
                                    &nbsp;&nbsp;• 삭제후, 회원 검색 및 복구가 불가능합니다!
                                    <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;( 회원이 강사코드로 재연결 가능 )
                                </Body3Regular>
                                <Body3Regular>
                                    &nbsp;&nbsp;• 삭제된 회원의 수강권은 자동으로 <Body3Bold>만료</Body3Bold>처리{' '}
                                    됩니다!
                                </Body3Regular>
                                <Body3Regular>
                                    &nbsp;&nbsp;• 삭제된 회원은 라포 앱 이용이 <Body3Bold>제한</Body3Bold> 될 수
                                    있습니다!
                                </Body3Regular>
                                <Body3Regular>
                                    &nbsp;&nbsp;• 회원님이 수업이 끝나도 수업일지 열람을 원하신다면
                                    <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;'<Body3Bold>담당강사 변경</Body3Bold>'을 통해서
                                    인계해주세요.
                                </Body3Regular>
                            </Col>
                            <Row>
                                <WhiteSquareButton
                                    text={'취소'}
                                    size="middle"
                                    mr={8}
                                    callBack={() => {
                                        setIsVisibleDeleteMember(false);
                                    }}
                                />
                                <ColorSquareButton
                                    text={'삭제'}
                                    size="middle"
                                    callBack={() => {
                                        onDeleteSelectedMember();
                                        setIsVisibleDeleteMember(false);
                                    }}
                                />
                            </Row>
                        </Col>
                    }
                />
            )}
            {isVisibleMultiCoachChange && (
                <ModalTrainerInfomationMemberListMultiCoachChange
                    setIsVisible={setIsVisibleMultiCoachChange}
                    selectedMemberCount={selectedMemberList.length}
                    isSubmitting={patchChangeCoachForMembersMutation.isLoading}
                    onClickChangeCoach={startBulkChange}
                    dropDownCoachList={dropDownCoachList}
                    selectCoach={selectCoach}
                />
            )}
            {isProcessing && (
                <ModalMemberListProgress
                    progress={progress}
                    memberCount={lastBulkChangeRequestRef.current?.memberIds.length || selectedMemberList.length}
                    isWaiting={isVirtualComplete && !isApiComplete}
                />
            )}
            {bulkChangeResult && (
                <BulkChangeCoachResultModal
                    result={bulkChangeResult.result}
                    recoveryNotice={bulkChangeResult.recoveryNotice}
                    onClose={() => setBulkChangeResult(null)}
                    onSelectFailed={() => {
                        const failedIds = bulkChangeResult.result.failed
                            .map((item) => item.memberId)
                            .filter(Boolean);
                        // 실패 회원을 특정할 수 없으면 기존 선택을 지우지 않고 닫기만 한다.
                        if (failedIds.length === 0) {
                            setBulkChangeResult(null);
                            return;
                        }
                        const failedIdSet = new Set(failedIds);
                        const memberById = new Map(
                            [...allMembers, ...selectedMemberList].map((member) => [member.memberId, member])
                        );
                        setSelectedMemberKeys(failedIdSet);
                        setSelectedMemberList(
                            failedIds
                                .map((memberId) => memberById.get(memberId))
                                .filter((member): member is ICenterMember => !!member)
                        );
                        setBulkChangeResult(null);
                    }}
                />
            )}
            {bulkChangeFailure && (
                <BulkChangeCoachFailureModal
                    failure={bulkChangeFailure.failure}
                    isRetrying={patchChangeCoachForMembersMutation.isLoading}
                    inquiryInfo={{
                        memberName:
                            Array.from(bulkChangeMemberNamesRef.current.values()).join(', ') || '이름 미확인',
                        memberId:
                            Array.from(bulkChangeMemberNamesRef.current.keys()).join(', ') || '확인 불가',
                        centerId: authObject?.centerId || '확인 불가',
                        occurredAt: dayjs(bulkChangeFailure.occurredAt).format('YYYY-MM-DD HH:mm:ss'),
                        errorCode: getChangeCoachErrorCode(bulkChangeFailure.failure),
                    }}
                    onRetry={() => {
                        const request = lastBulkChangeRequestRef.current;
                        if (request) runBulkChangeRequest(request);
                    }}
                    onRefresh={() => {
                        void refetchPublicMemberList().finally(() => setBulkChangeFailure(null));
                    }}
                    onClose={() => setBulkChangeFailure(null)}
                />
            )}
        </Col>
    );
};

export default MemberList;
